import { DocumentControlActions } from '@readcloud/data';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';
import React from 'react';

export const usePDFGridNavigation = (props: {
  flow: 'Horizontal' | 'Vertical';
  containerRef: React.RefObject<HTMLDivElement>;
  cellOffsets: {
    [pageNumber: string]: number;
  };
  measuredCells: {
    [pageNumber: string]: {
      size: {
        width: number;
        height: number;
      };
    };
  };
  leftMargin: number;
  topMargin: number;
  outerWidth: number;
  outerHeight: number;
  viewScale: number;
  continuous: boolean;
  gutterSize: number;
  unscaledGutterSize: number;
  mostCommonWidth: number;
  mostCommonHeight: number;
  containerSize: {
    width: number;
    height: number;
  };
  screenOrigin: {
    x: number;
    y: number;
  };
  prevScrollPosition: {
    x: number;
    y: number;
  };
  documentControl: DocumentControlActions;
  location: string;
  setLocation: (location: string) => void;
  pdfDocument: PDFDocumentProxy;
  linkService: PDFLinkService;
}) => {
  const [initialLoad, setInitialLoad] = React.useState<boolean>(true);
  const [scrollPercentage, setScrollPercentage] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const prevViewScaleRef = React.useRef<number>(1);
  const prevContainerSizeRef = React.useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  // ######################################################
  // Functions for navigating pages
  //#######################################################
  //simple scroll to location that takes into account flow.
  const ScrollToLoc = React.useCallback(
    (offset: number, cb?: () => void) => {
      if (!props.containerRef.current) {
        return;
      }
      if (props.flow === 'Horizontal') {
        props.containerRef.current.scrollTo({
          left: Math.ceil(offset),
        });
      } else {
        props.containerRef.current.scrollTo({
          top: Math.ceil(offset),
        });
      }

      cb && cb();
    },
    [props.flow, props.containerRef]
  );

  //convert page number to scroll location
  const PageToLocation = React.useCallback(
    (pageNumber: number) => {
      // work out estimated page position based off closest cell offset +/- estimated page sizes.
      const closestPage =
        (Object.keys(props.cellOffsets).length &&
          Object.keys(props.cellOffsets)?.reduce(
            (prev: string, curr: string) => {
              return Math.abs(parseInt(curr) - pageNumber) <
                Math.abs(parseInt(prev) - pageNumber)
                ? curr
                : prev;
            }
          )) ||
        '0';

      let closestOffset =
        props.flow === 'Horizontal'
          ? props.cellOffsets[closestPage] + props.leftMargin
          : props.cellOffsets[closestPage] + props.topMargin;

      if (props.flow === 'Horizontal') {
        const closestPageDimensions =
          props.measuredCells[closestPage]?.size.width * props.viewScale || 0;
        closestOffset =
          closestOffset - props.outerWidth / 2 + closestPageDimensions / 2;
      } else {
        const closestPageDimensions =
          props.measuredCells[closestPage]?.size.height * props.viewScale || 0;
        closestOffset =
          closestOffset - props.outerHeight / 2 + closestPageDimensions / 2;
      }

      const pageDifference = pageNumber - parseInt(closestPage);

      const cellWidth = props.continuous
        ? props.mostCommonWidth * props.viewScale + props.gutterSize
        : Math.max(
            props.mostCommonWidth * props.viewScale + props.gutterSize,
            props.outerWidth
          );

      const cellHeight = props.continuous
        ? props.mostCommonHeight * props.viewScale + props.gutterSize
        : Math.max(
            props.mostCommonHeight * props.viewScale + props.gutterSize,
            props.outerHeight
          );

      const differenceFromClosestOffset =
        props.flow === 'Horizontal'
          ? pageDifference * cellWidth
          : pageDifference * cellHeight;

      return closestOffset + differenceFromClosestOffset;
    },
    [
      props.cellOffsets,
      props.measuredCells,
      props.viewScale,
      props.gutterSize,
      props.flow,
      props.topMargin,
      props.leftMargin,
      props.mostCommonWidth,
      props.mostCommonHeight,
      props.outerHeight,
      props.outerWidth,
    ]
  );

  //convert scroll location to page number
  const LocationToPage = React.useCallback(
    (location: number) => {
      const closestPage =
        (Object.keys(props.cellOffsets).length &&
          Object.keys(props.cellOffsets)?.reduce(
            (prev: string, curr: string) => {
              return Math.abs(props.cellOffsets[curr] - location) <
                Math.abs(props.cellOffsets[prev] - location)
                ? curr
                : prev;
            }
          )) ||
        '0';

      let closestOffset =
        props.flow === 'Horizontal'
          ? props.cellOffsets[closestPage] + props.leftMargin
          : props.cellOffsets[closestPage] + props.topMargin;

      if (props.flow === 'Horizontal') {
        const closestPageDimensions =
          props.measuredCells[closestPage]?.size.width * props.viewScale || 0;
        closestOffset =
          closestOffset - props.outerWidth / 2 + closestPageDimensions / 2;
      } else {
        const closestPageDimensions =
          props.measuredCells[closestPage]?.size.height * props.viewScale || 0;
        closestOffset =
          closestOffset - props.outerHeight / 2 + closestPageDimensions / 2;
      }

      const offsetDifference = location - closestOffset;

      let pageDifference = 0;

      if (props.flow === 'Horizontal') {
        const cellWidth = props.continuous
          ? props.mostCommonWidth * props.viewScale + props.gutterSize
          : Math.max(
              props.outerWidth,
              props.mostCommonWidth * props.viewScale + props.gutterSize
            );

        pageDifference = Math.round(offsetDifference / cellWidth);
      } else {
        const cellHeight = props.continuous
          ? props.mostCommonHeight * props.viewScale + props.gutterSize
          : Math.max(
              props.outerHeight,
              props.mostCommonHeight * props.viewScale + props.gutterSize
            );

        pageDifference = Math.round(offsetDifference / cellHeight);
      }

      return Math.max(1, Math.floor(pageDifference + parseInt(closestPage)));
    },
    [
      props.cellOffsets,
      props.measuredCells,
      props.viewScale,
      props.gutterSize,
      props.flow,
      props.topMargin,
      props.leftMargin,
      props.mostCommonWidth,
      props.mostCommonHeight,
      props.outerHeight,
      props.outerWidth,
    ]
  );

  //##################################################
  //On Load - Scroll to page once containerSize has been calculated.
  //##################################################
  React.useEffect(() => {
    if (initialLoad) {
      if (
        props.containerSize.width > 0 &&
        props.containerSize.height > 0 &&
        parseInt(props.location)
      ) {
        const loc = PageToLocation(parseInt(props.location));
        ScrollToLoc(loc);

        //set this here so the zoom logic doesnt kick in instantly
        prevViewScaleRef.current = props.viewScale;
        setInitialLoad(false);
      }
    }
  }, [props.containerSize, initialLoad, props.viewScale]);

  //##################################################
  //Update Location (pageNumber) if scroll position changes
  //##################################################
  React.useEffect(() => {
    //figure out the pageNumber based on scroll position
    const handleUpdatingPageNumber = () => {
      let lastScrollPos: { x: number; y: number } = { x: 0, y: 0 };
      lastScrollPos = {
        x: props.containerRef?.current?.scrollLeft - props.leftMargin || 0,
        y: props.containerRef?.current?.scrollTop - props.topMargin || 0,
      };
      let totalScrollSize = {
        x: props.containerSize.width || 0,
        y: props.containerSize.height || 0,
      };

      const scrollPos =
        props.flow === 'Horizontal' ? lastScrollPos.x : lastScrollPos.y;

      const tempPageNumber = LocationToPage(
        props.flow === 'Horizontal'
          ? scrollPos + props.leftMargin
          : scrollPos + props.topMargin
      );

      if (tempPageNumber.toString() !== props.location.toString()) {
        props.setLocation(tempPageNumber.toString());
      }

      //use this opportunity to also track scroll percentage.
      setScrollPercentage({
        x:
          Math.round(lastScrollPos.x * 100) /
          100 /
          (Math.round(totalScrollSize.x * 100) / 100 || 1),
        y:
          Math.round(lastScrollPos.y * 100) /
          100 /
          (Math.round(totalScrollSize.y * 100) / 100 || 1),
      });
    };

    props.containerRef?.current?.addEventListener(
      'scroll',
      handleUpdatingPageNumber
    );
    return () => {
      props.containerRef.current &&
        props.containerRef.current.removeEventListener(
          'scroll',
          handleUpdatingPageNumber
        );
    };
  }, [props.flow, props.gutterSize, props.containerRef, props.containerSize]);

  //##################################################
  // Zooming - we want to stay at the same position.
  //##################################################
  React.useEffect(() => {
    if (!initialLoad) {
      if (props.viewScale !== prevViewScaleRef.current) {
        //using viewScale to derive the transform amount doesnt actually work, because of things like margins that doesnt scale with viewscale.
        const transformAmount =
          props.flow === 'Horizontal'
            ? props.containerSize.width / prevContainerSizeRef.current.width
            : props.containerSize.height / prevContainerSizeRef.current.height;

        //these scroll variables do not include previous margin.
        //i.e. prevScrollPosition.x = outerContainer.scrollLeft - innerContainer.offsetLeft
        const scrollLeftWithoutMargin =
          props.prevScrollPosition.x || -props.leftMargin;
        const scrollTopWithoutMargin =
          props.prevScrollPosition.y || -props.topMargin;
        //now we can transform that scroll, and add our current margins to it.
        //this makes it feel like the scroll position hasn't changed

        const newScrollPos = {
          x: Math.round(
            scrollLeftWithoutMargin * transformAmount +
              props.leftMargin +
              (props.screenOrigin.x * transformAmount - props.screenOrigin.x)
          ),
          y: Math.round(
            scrollTopWithoutMargin * transformAmount +
              props.topMargin +
              (props.screenOrigin.y * transformAmount - props.screenOrigin.y)
          ),
        };

        props.containerRef.current.scrollTo(newScrollPos.x, newScrollPos.y);
      }

      //this is how we are storing the previous viewscale to use in our calculations above.
      prevViewScaleRef.current = props.viewScale;
      prevContainerSizeRef.current = props.containerSize;
    }
  }, [
    props.screenOrigin.x,
    props.screenOrigin.y,
    props.viewScale,
    props.containerSize.width,
    props.containerSize.height,
    props.prevScrollPosition.x,
    props.prevScrollPosition.y,
  ]);

  //#######################################################
  // Update the scroll position if we change flow, guttersize
  //#######################################################
  const prevFlowRef = React.useRef(props.flow);
  React.useEffect(() => {
    let totalScrollSize = {
      x: props.containerSize.width || 0,
      y: props.containerSize.height || 0,
    };

    if (props.flow != prevFlowRef.current) {
      props.containerRef.current.scrollTo(
        totalScrollSize.x * scrollPercentage.y + props.leftMargin,
        totalScrollSize.y * scrollPercentage.x + props.topMargin
      );
      prevFlowRef.current = props.flow;
    } else {
      props.containerRef.current.scrollTo(
        totalScrollSize.x * scrollPercentage.x + props.leftMargin,
        totalScrollSize.y * scrollPercentage.y + props.topMargin
      );
    }
  }, [props.flow, props.unscaledGutterSize]);

  //#######################################################
  // If we change continuous, we want to jump to the current page for better UX (instead of landing in a gap between pages)
  //#######################################################
  React.useEffect(() => {
    const loc = PageToLocation(parseInt(props.location));
    ScrollToLoc(loc);
  }, [props.continuous]);

  //#######################################################
  // Navigation Controls Handler
  //#######################################################
  React.useEffect(() => {
    if (props.pdfDocument && props.documentControl?.type) {
      switch (props.documentControl?.type) {
        case 'changeLocation': {
          const loc = PageToLocation(
            parseInt(props.documentControl.payload.location, 10)
          );
          ScrollToLoc(loc);
          break;
        }
        case 'gotoToc': {
          props.linkService.goToDestination(props.documentControl.payload.dest);
          break;
        }
        case 'next': {
          const loc = PageToLocation(
            Math.max(
              1,
              Math.min(props.pdfDocument.numPages, parseInt(props.location) + 1)
            )
          );

          ScrollToLoc(loc);
          break;
        }
        case 'prev': {
          const loc = PageToLocation(
            Math.max(
              1,
              Math.min(props.pdfDocument.numPages, parseInt(props.location) - 1)
            )
          );
          ScrollToLoc(loc);
          break;
        }
      }
    }
  }, [props.pdfDocument, props.documentControl]);
};
